import React, { useState, useEffect } from 'react';
import { CalendarDayTooltip } from './CalendarDayTooltip';
import { ClientDaySummaryModal } from './ClientDaySummaryModal';
import { healthAPI } from '../../utils/apiClient';
import { useUserTimezone } from '../../hooks/useUserTimezone';
import { isToday as isTodayInTz } from '../../utils/timezoneHelpers';
import { getStatusColor as getScoreStatusColor, SCORE_COLORS } from '../../../config/scoring';

export interface GoalTarget {
  goal_value: number;
  goal_type: 'over' | 'under' | 'target' | 'range' | 'boolean' | 'tracking_only';
  variance_percent?: number;
  floor_value?: number;
  visibility?: string;
  status?: string;
  tier?: string;
}

export interface DailyHealthData {
  date: string;
  weight?: number;
  steps: number;
  calories: number;
  sleep: number;
  water: number;
  workouts: number;
  energy_level?: number;
  stress_level?: number;
  notes?: string;
  score?: number;
  status?: string;
  active_goals_snapshot?: string[];
  tierStatus?: 'tier3' | 'tier2' | 'tier1' | 'none';
  softScore?: number | null;
  targets_at_time?: Record<string, GoalTarget>;
  actual_values?: {
    calories?: number;
    protein_g?: number;
    carbs_g?: number;
    fats_g?: number;
    fiber_g?: number;
    sodium_mg?: number;
    potassium_mg?: number;
    steps?: number;
    sleep_hours?: number;
    water_intake?: number;
    energy_level?: number;
    stress_level?: number;
    weight?: number;
    [key: string]: number | undefined;
  };
}

interface HealthDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  score: number;
  status: 'excellent' | 'good' | 'fair' | 'atRisk' | 'needsSupport' | 'no-data';
  data?: DailyHealthData;
}

interface HealthCalendarTileProps {
  onExpand?: () => void;
  userId?: string; // If provided, view calendar for this user (coach view)
  clientTimezone?: string; // Client's IANA timezone — used for day boundaries in coach view
  readOnly?: boolean; // If true, disable interactions
  onDayClick?: (date: string, data: DailyHealthData | null, ctrlKey: boolean) => void;
  onDataLoaded?: (data: DailyHealthData[]) => void;
  initialMonth?: number;  // 0-indexed
  initialYear?: number;
  onMonthChange?: (month: number, year: number) => void;
  // Visual selection highlighting (controlled by parent expanded view)
  highlightedDate?: string | null;       // Single day highlight (YYYY-MM-DD)
  highlightedWeekStart?: string | null;  // Week highlight - Monday date (YYYY-MM-DD)
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const HealthCalendarTile: React.FC<HealthCalendarTileProps> = ({ onExpand: _onExpand, userId, clientTimezone, readOnly: _readOnly, onDayClick, onDataLoaded, initialMonth, initialYear, onMonthChange, highlightedDate, highlightedWeekStart }) => {
  const { isToday: isTodayInUserTz } = useUserTimezone();
  // Use client's timezone for day boundaries when viewing client data
  const isTodayForClient = clientTimezone
    ? (dateString: string) => isTodayInTz(dateString, clientTimezone)
    : isTodayInUserTz;
  const today = new Date();

  // Initialize month/year from localStorage (client view only) or default to current month
  // Coach view (userId provided) always starts on current month — no stale localStorage leakage
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (initialMonth != null) return initialMonth;
    const savedMonth = localStorage.getItem('healthCalendarCurrentMonth');
    const result = (userId) ? today.getMonth() : (savedMonth !== null ? parseInt(savedMonth, 10) : today.getMonth());
    return result;
  });
  const [currentYear, setCurrentYear] = useState(() => {
    if (initialYear != null) return initialYear;
    const savedYear = localStorage.getItem('healthCalendarCurrentYear');
    const result = (userId) ? today.getFullYear() : (savedYear !== null ? parseInt(savedYear, 10) : today.getFullYear());
    return result;
  });
  const [healthData, setHealthData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [userGoals, setUserGoals] = useState<any>(null); // PHASE 5E: Store user goals for tier coloring
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    data: DailyHealthData | null;
    x: number;
    y: number;
  }>({
    visible: false,
    data: null,
    x: 0,
    y: 0
  });
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);

  // Phase 2 Client Context: Selected day for modal display (client view only)
  const [selectedDay, setSelectedDay] = useState<DailyHealthData | null>(null);
  const [previousDayWeight, setPreviousDayWeight] = useState<number | undefined>(undefined);

  // PHASE 5E: Toggle for showing all metrics - available to both clients and coaches
  const [showAllMetrics, setShowAllMetrics] = useState(() => {
    // Use different localStorage keys for coach vs client
    const storageKey = userId ? 'coach_calendar_show_all_metrics' : 'client_calendar_show_all_metrics';
    const stored = localStorage.getItem(storageKey);
    return stored === 'true';
  });

  // PHASE 5E: Toggle for hiding soft score when it matches hard score
  const [hideMatchingSoftScore, setHideMatchingSoftScore] = useState(() => {
    // Use different localStorage keys for coach vs client
    const storageKey = userId ? 'coach_calendar_hide_matching_soft_score' : 'client_calendar_hide_matching_soft_score';
    const stored = localStorage.getItem(storageKey);
    return stored === 'true';
  });

  // Save preference to localStorage when it changes
  useEffect(() => {
    const storageKey = userId ? 'coach_calendar_show_all_metrics' : 'client_calendar_show_all_metrics';
    localStorage.setItem(storageKey, String(showAllMetrics));
  }, [showAllMetrics, userId]);

  // Save hide matching soft score preference to localStorage when it changes
  useEffect(() => {
    const storageKey = userId ? 'coach_calendar_hide_matching_soft_score' : 'client_calendar_hide_matching_soft_score';
    localStorage.setItem(storageKey, String(hideMatchingSoftScore));
  }, [hideMatchingSoftScore, userId]);

  // Persist current month/year to localStorage when they change (client view only)
  useEffect(() => {
    if (!userId) {
      localStorage.setItem('healthCalendarCurrentMonth', String(currentMonth));
      localStorage.setItem('healthCalendarCurrentYear', String(currentYear));
    }
  }, [currentMonth, currentYear, userId]);

  // Fetch health data and goals on component mount
  useEffect(() => {
    // AbortController for cleanup - prevents state updates after unmount
    // and handles React 18 StrictMode double-mounting
    const abortController = new AbortController();
    let isMounted = true;
    const effectId = Math.random().toString(36).substr(2, 5);

    console.log(`[Calendar ${effectId}] Effect starting for month=${currentMonth}, year=${currentYear}, userId=${userId}`);

    // Set loading immediately (synchronously) to prevent stale data flash
    setLoading(true);

    const fetchHealthData = async () => {
      console.log(`[Calendar ${effectId}] Fetching data...`);

      // If userId is provided (coach view), use coach endpoint
      if (userId) {
        const token = localStorage.getItem('auth_token');

        // Fetch calendar data (separate try/catch to prevent goals error from clearing data)
        try {
          const response = await fetch(
            `/api/coach/clients/${userId}/metrics/calendar?month=${currentMonth + 1}&year=${currentYear}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              signal: abortController.signal
            }
          );
          const data = await response.json();
          console.log(`[Calendar ${effectId}] API response: success=${data.success}, dataCount=${data.data?.healthData?.length || 0}, isMounted=${isMounted}`);
          if (isMounted && data.success) {
            const loadedData = data.data.healthData || [];
            setHealthData(loadedData);
            onDataLoaded?.(loadedData);
            console.log(`[Calendar ${effectId}] Set healthData with ${loadedData.length} records`);
          } else if (!data.success) {
            console.log(`[Calendar ${effectId}] API returned success=false:`, data);
            if (isMounted) setHealthData([]);
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            return; // Exit early on abort
          }
          console.error('Failed to fetch calendar health data:', error);
          if (isMounted) setHealthData([]);
        }

        // PHASE 5E: Fetch client goals for tier coloring (separate try/catch)
        try {
          const goalsResponse = await fetch(
            `/api/coach/clients/${userId}/goals`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              signal: abortController.signal
            }
          );
          const goalsData = await goalsResponse.json();
          if (isMounted && goalsData.success) {
            setUserGoals(goalsData.goals);
          }
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('Failed to fetch goals (non-critical):', error);
          }
          // Goals fetch failure should NOT clear calendar data
        }
      } else {
        // Use regular client endpoint (separate try/catch blocks)
        try {
          const response = await healthAPI.getCalendarData(currentMonth, currentYear);
          if (isMounted && response.success) {
            setHealthData(response.data.healthData || []);
          } else if (isMounted) {
            setHealthData([]);
          }
        } catch (error: any) {
          console.error('Failed to fetch calendar health data:', error);
          if (isMounted) setHealthData([]);
        }

        // PHASE 5E: Fetch user goals for tier coloring (separate try/catch)
        try {
          const goalsResponse = await healthAPI.getGoals();
          if (isMounted && goalsResponse.success) {
            setUserGoals(goalsResponse.goals);
          }
        } catch (error: any) {
          console.error('Failed to fetch goals (non-critical):', error);
          // Goals fetch failure should NOT clear calendar data
        }
      }

      // Set loading false at the end
      if (isMounted) {
        setLoading(false);
      }
    };

    fetchHealthData();

    // Cleanup: abort fetch and prevent state updates
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [currentMonth, currentYear, userId]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const handleMouseEnter = (event: React.MouseEvent, day: HealthDay) => {
    if (!day.data || !day.isCurrentMonth) return;

    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }

    // Use mouse position for positioning to the left
    const mouseX = event.clientX;
    const mouseY = event.clientY;

    // Set a new timeout for 500ms
    const timeout = setTimeout(() => {
      setTooltip({
        visible: true,
        data: day.data!,
        x: mouseX,
        y: mouseY
      });
    }, 500);

    setHoverTimeout(timeout);
  };

  const handleMouseLeave = () => {
    // Clear timeout and hide tooltip
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const goToPreviousMonth = () => {
    const newMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const newYear = currentMonth === 0 ? currentYear - 1 : currentYear;
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    onMonthChange?.(newMonth, newYear);
  };

  const goToNextMonth = () => {
    const newMonth = currentMonth === 11 ? 0 : currentMonth + 1;
    const newYear = currentMonth === 11 ? currentYear + 1 : currentYear;
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    onMonthChange?.(newMonth, newYear);
  };

  const goToToday = () => {
    const now = new Date();
    const newMonth = now.getMonth();
    const newYear = now.getFullYear();
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    onMonthChange?.(newMonth, newYear);
  };

  const getStatusColor = (status: HealthDay['status']): string => {
    if (status === 'no-data' || !status) return 'transparent';
    return getScoreStatusColor(status);
  };

  /**
   * PHASE 5E: Get cell background color based on tier status
   * Priority: Tier 3 (scoring) > Tier 2 (practice) > Tier 1 (coach-only) > none
   * Falls back to legacy behavior if tierStatus not available
   *
   * Client View T1 Behavior: Dark muted purple (indicates data exists, toggle to see)
   * Coach View T1 Behavior: Bright purple (indicates T1 goal being tracked)
   */
  const getCellBackgroundColor = (day: HealthDay) => {
    if (!day.isCurrentMonth) {
      return 'var(--bg-secondary)';
    }

    // Today with data = in-progress (visual handled via CSS class, no score color)
    if (day.isToday && day.data) {
      return 'var(--bg-primary)';
    }

    // No data at all - empty cell
    if (!day.data) {
      return 'var(--bg-primary)';
    }

    const tierStatus = day.data.tierStatus;

    // PHASE 5E: Tier-based coloring (if tierStatus is available)
    if (tierStatus) {
      // Tier 3: Use scoring colors (green to red based on score)
      if (tierStatus === 'tier3') {
        return day.status !== 'no-data' ? getStatusColor(day.status) : 'var(--bg-primary)';
      }

      // Tier 2: Violet (practice mode - visible but not scored)
      if (tierStatus === 'tier2') {
        return '#9C27B0'; // Bright violet
      }

      // Tier 1: Different colors for coach vs client view
      if (tierStatus === 'tier1') {
        // Coach view: Bright purple (indicates actively tracked T1 goal)
        if (userId) {
          return '#673AB7'; // Bright dark purple (--tier-calendar-1)
        }
        // Client view: Very dark muted purple (subtle "data exists here" indicator)
        return '#3d2566'; // 40% darker than coach purple
      }

      // tier status is 'none'
      return 'var(--bg-primary)';
    }

    // LEGACY BEHAVIOR: No tierStatus (old data or pre-Phase 5E)
    // Use the original scoring color logic
    if (day.status !== 'no-data') {
      return getStatusColor(day.status);
    }

    // Check for untracked data (has data but no goals)
    const hasUntrackedData = day.status === 'no-data' &&
      (!day.data.active_goals_snapshot || day.data.active_goals_snapshot.length === 0);

    return hasUntrackedData ? 'var(--purple-subtle)' : 'var(--bg-primary)';
  };

  const generateCalendarDays = (): HealthDay[] => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const firstCalendarDay = new Date(firstDay);
    firstCalendarDay.setDate(firstDay.getDate() - firstDay.getDay());

    const days: HealthDay[] = [];

    // Generate 42 days (6 weeks) for full calendar view in 3x3 tile
    for (let i = 0; i < 42; i++) {
      const currentDate = new Date(firstCalendarDay);
      currentDate.setDate(firstCalendarDay.getDate() + i);

      // Use timezone-aware date formatting
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;

      const dayHealthData = healthData.find(d => d.date === dateString);

      days.push({
        date: currentDate,
        isCurrentMonth: currentDate.getMonth() === currentMonth,
        isToday: isTodayForClient(dateString),
        score: dayHealthData?.score || 0,
        status: dayHealthData?.status || 'no-data',
        data: dayHealthData
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  return (
    <div
      className="health-calendar-tile progress-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '16px',
        cursor: 'default',
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-secondary)',
        borderRadius: 'var(--radius-large)',
        position: 'relative'
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            margin: '0',
            color: 'var(--text-primary)'
          }}>
            Health Calendar
          </h3>

          {/* PHASE 5E: Show ALL metrics toggle (available to clients and coaches) */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginLeft: 'auto' }}>
            <span style={{
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap'
            }}>
              Show ALL metrics
            </span>
              <div
                onClick={() => setShowAllMetrics(!showAllMetrics)}
                style={{
                  width: '36px',
                  height: '20px',
                  borderRadius: '10px',
                  backgroundColor: showAllMetrics ? 'var(--accent-blue)' : 'var(--border-color)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent',
                  boxSizing: 'border-box',
                  flexShrink: 0
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: showAllMetrics ? '18px' : '2px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }}
                />
              </div>
            </label>

          {/* PHASE 5E: Hide matching soft score toggle */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <span style={{
              fontSize: '12px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap'
            }}>
              Hide matching soft scores
            </span>
              <div
                onClick={() => setHideMatchingSoftScore(!hideMatchingSoftScore)}
                style={{
                  width: '36px',
                  height: '20px',
                  borderRadius: '10px',
                  backgroundColor: hideMatchingSoftScore ? 'var(--accent-blue)' : 'var(--border-color)',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  border: '2px solid transparent',
                  boxSizing: 'border-box',
                  flexShrink: 0
                }}
              >
                <div
                  style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    position: 'absolute',
                    top: '2px',
                    left: hideMatchingSoftScore ? '18px' : '2px',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                  }}
                />
              </div>
            </label>
        </div>

        {/* Month Navigation Controls */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0
        }}>
          {/* Previous Month Button */}
          <button
            onClick={goToPreviousMonth}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px'
            }}
            title="Previous month"
          >
            ‹
          </button>

          {/* Month/Year Display (clickable to go to today) */}
          <button
            onClick={goToToday}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              cursor: 'pointer',
              padding: '4px 8px',
              fontSize: '13px',
              fontWeight: '500',
              borderRadius: '4px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            title="Go to current month"
          >
            {MONTHS[currentMonth]} {currentYear}
          </button>

          {/* Next Month Button */}
          <button
            onClick={goToNextMonth}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              fontSize: '14px'
            }}
            title="Next month"
          >
            ›
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          fontSize: '14px',
          color: 'var(--text-secondary)'
        }}>
          Loading health data...
        </div>
      )}

      {/* Calendar Grid */}
      {!loading && (
        <>
          <div style={{
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gridTemplateRows: 'auto repeat(6, 1fr)',
            gap: '2px',
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '4px',
            overflow: 'hidden'
          }}>
            {/* Weekday Headers */}
            {WEEKDAYS.map((day, index) => (
              <div
                key={`weekday-${index}`}
                style={{
                  backgroundColor: 'var(--bg-tertiary)',
                  padding: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  color: 'var(--text-secondary)',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '20px'
                }}
              >
                {day}
              </div>
            ))}

            {/* Calendar Days */}
            {calendarDays.map((day, index) => {
              // PHASE 5E: Use tier-aware cell coloring
              const cellBackgroundColor = getCellBackgroundColor(day);
              const tierStatus = day.data?.tierStatus;

              const isInProgress = day.isToday && !!day.data;

              // Text should be white for colored cells (Tier 2, Tier 3 scoring, or Tier 1 if coach view)
              const shouldUseWhiteText = !isInProgress && (
                tierStatus === 'tier2' ||
                tierStatus === 'tier1' ||
                (tierStatus === 'tier3' && day.status !== 'no-data')
              );

              // Selection highlighting (from parent expanded view)
              const dayDateStr = `${day.date.getFullYear()}-${String(day.date.getMonth() + 1).padStart(2, '0')}-${String(day.date.getDate()).padStart(2, '0')}`;
              const isDayHighlighted = highlightedDate === dayDateStr;
              const isInHighlightedWeek = (() => {
                if (!highlightedWeekStart) return false;
                const weekStart = new Date(highlightedWeekStart + 'T12:00:00');
                const weekEnd = new Date(weekStart);
                weekEnd.setDate(weekEnd.getDate() + 6); // Monday + 6 = Sunday
                const d = new Date(dayDateStr + 'T12:00:00');
                return d >= weekStart && d <= weekEnd;
              })();
              // Determine which days in the week row get left/right border radius
              const isWeekStart = highlightedWeekStart === dayDateStr;
              const isWeekEnd = (() => {
                if (!highlightedWeekStart) return false;
                const weekEnd = new Date(highlightedWeekStart + 'T12:00:00');
                weekEnd.setDate(weekEnd.getDate() + 6);
                return dayDateStr === `${weekEnd.getFullYear()}-${String(weekEnd.getMonth() + 1).padStart(2, '0')}-${String(weekEnd.getDate()).padStart(2, '0')}`;
              })();

              // Build border style — bright blue (#3b82f6) to stand out from tier colors
              let borderStyle = 'none';
              if (isDayHighlighted) {
                borderStyle = '3px solid #3b82f6';
              }

              // Build box-shadow for week highlight (bright blue, very pronounced)
              let boxShadow = 'none';
              if (isInHighlightedWeek && !isDayHighlighted) {
                boxShadow = 'inset 0 3px 0 0 #3b82f6, inset 0 -3px 0 0 #3b82f6';
                if (isWeekStart) boxShadow += ', inset 3px 0 0 0 #3b82f6';
                if (isWeekEnd) boxShadow += ', inset -3px 0 0 0 #3b82f6';
              }

              return (
                <div
                  key={index}
                  style={{
                    backgroundColor: !day.isCurrentMonth ? 'var(--bg-secondary)' : cellBackgroundColor,
                    ...(isInProgress ? {
                      background: 'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(59, 130, 246, 0.4) 4px, rgba(59, 130, 246, 0.4) 8px)',
                    backgroundAttachment: 'fixed'
                    } : {}),
                    opacity: day.isCurrentMonth ? 1 : 0.4,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: day.isToday ? '600' : '400',
                    color: day.isCurrentMonth && shouldUseWhiteText ? 'white' : 'var(--text-primary)',
                    border: borderStyle,
                    boxShadow: boxShadow,
                    position: 'relative',
                    minHeight: '28px',
                    cursor: (userId && onDayClick) || (day.isCurrentMonth && day.data) ? 'pointer' : 'default'
                  }}
                  onMouseEnter={(e) => handleMouseEnter(e, day)}
                  onMouseLeave={handleMouseLeave}
                  onClick={(e) => {
                    // Coach view: fire onDayClick for any click (empty/invalid = null for deselect)
                    if (userId && onDayClick) {
                      if (!day.isCurrentMonth || !day.data) {
                        onDayClick('', null, false);
                      } else {
                        const dateStr = day.date.toISOString().split('T')[0];
                        onDayClick(dateStr, day.data, e.ctrlKey || e.metaKey);
                      }
                      return;
                    }
                    if (!day.isCurrentMonth) return;
                    // Client view: Open day summary modal on click
                    if (!userId && day.data) {
                      setSelectedDay(day.data);
                      // Find previous day's weight for delta display
                      const clickedDate = new Date(day.data.date);
                      const prevDate = new Date(clickedDate);
                      prevDate.setDate(prevDate.getDate() - 1);
                      const prevDateStr = prevDate.toISOString().split('T')[0];
                      const prevDayData = healthData.find(d => d.date === prevDateStr);
                      const prevWeight = prevDayData?.weight || prevDayData?.actual_values?.weight;
                      setPreviousDayWeight(prevWeight);
                    }
                  }}
                >
                  {day.date.getDate()}
              </div>
              );
            })}
          </div>

          {/* Compact Legend */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '6px',
            fontSize: '10px',
            color: 'var(--text-secondary)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: SCORE_COLORS.excellent,
                borderRadius: '1px'
              }} />
              <span>Excellent</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: SCORE_COLORS.good,
                borderRadius: '1px'
              }} />
              <span>Good</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: SCORE_COLORS.fair,
                borderRadius: '1px'
              }} />
              <span>Fair</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: SCORE_COLORS.atRisk,
                borderRadius: '1px'
              }} />
              <span>At Risk</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <div style={{
                width: '6px',
                height: '6px',
                backgroundColor: SCORE_COLORS.needsSupport,
                borderRadius: '1px'
              }} />
              <span>Needs Support</span>
            </div>
          </div>
        </>
      )}

      {/* Tooltip - render outside container to avoid overflow issues */}
      {tooltip.data && (
        <CalendarDayTooltip
          data={tooltip.data}
          visible={tooltip.visible}
          x={tooltip.x}
          y={tooltip.y}
          showAllMetrics={showAllMetrics}
          hideMatchingSoftScore={hideMatchingSoftScore} // PHASE 5E: Hide soft score when it matches hard score
          userGoals={userId ? userGoals : null} // COACH VIEW: Pass userGoals when viewing client (userId present), CLIENT VIEW: null to use historical tier
          isCoachView={!!userId} // NEW: Explicit coach context indicator
        />
      )}

      {/* Phase 2 Client Context: Day Summary Modal (client view only) */}
      {selectedDay && !userId && (
        <ClientDaySummaryModal
          data={selectedDay}
          onClose={() => {
            setSelectedDay(null);
            setPreviousDayWeight(undefined);
          }}
          previousWeight={previousDayWeight}
        />
      )}
    </div>
  );
};